import React, { useMemo, useRef, useState } from 'react';
import Slider, { SliderProps } from 'rc-slider';
import { SliderRef } from 'rc-slider/lib/Slider';
import classNames from 'classnames';
import { debounce, parseInt } from 'lodash';
import styles from './styles.module.scss';
import { TextField } from '../TextField/TextField';

const RcRangeSlider = Slider as React.ForwardRefExoticComponent<
  SliderProps<number[]> & React.RefAttributes<SliderRef>
>;

type Props = {
  className?: string;
  label: string;
  disabled?: boolean;
  max: number;
  min: number;
  step: number | null;
  value: [number, number];
  marks?: SliderProps['marks'];
  onChange?: (value: number[]) => any;
  debounceTimeMs: number;
  onDebounce?: (type: 'start' | 'end') => any;
};

export const RangeSlider = ({
  className,
  label,
  max,
  min,
  step,
  marks,
  onDebounce,
  value,
  onChange,
  debounceTimeMs,
}: Props) => {
  const [lowerBound, upperBound] = value;

  const [textOptions, setTextOptions] = useState({
    low: { value: lowerBound.toString(), enabled: false },
    high: { value: upperBound.toString(), enabled: false },
  });

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const isDebouncingRef = useRef(false);
  const debouncedChange = useMemo(
    () =>
      debounce((values: [number, number], dir: 'low' | 'high') => {
        const clampedValues = [values[0], values[1]];
        if (clampedValues[0] >= clampedValues[1]) {
          if (dir === 'low') {
            clampedValues[0] = clampedValues[1] - 1;
          } else {
            clampedValues[1] = clampedValues[0] + 1;
          }
        }
        onChangeRef.current?.(clampedValues);
        setTextOptions({
          low: { value: lowerBound.toString(), enabled: false },
          high: { value: upperBound.toString(), enabled: false },
        });
        isDebouncingRef.current = false;
        onDebounce?.('end');
      }, debounceTimeMs),
    [lowerBound, upperBound, onDebounce, debounceTimeMs],
  );

  const inputProps = {
    min,
    max,
    step,
    marks,
  };

  const handleChange = (
    values: { low: string; high: string },
    dir: 'low' | 'high',
  ) => {
    if (!isDebouncingRef.current) {
      isDebouncingRef.current = true;
      onDebounce?.('start');
    }
    setTextOptions((options) => {
      const newOptions = { ...options };
      newOptions.low.value = values.low.toString();
      newOptions.high.value = values.high.toString();
      return newOptions;
    });
    debouncedChange(
      [parseInt(values.low) || 0, parseInt(values.high) || 0],
      dir,
    );
  };

  const renderInput = () => {
    return (
      <RcRangeSlider
        range
        {...inputProps}
        allowCross={false}
        value={value}
        onChange={onChange}
      />
    );
  };

  const createTextField = (type: 'low' | 'high') => {
    const bound = type === 'low' ? lowerBound : upperBound;
    return (
      <TextField
        label=" "
        type="number"
        value={textOptions[type].enabled ? textOptions[type].value : bound}
        onChange={(e) => {
          setTextOptions((options) => ({
            ...options,
            [type]: { value: bound, enabled: true },
          }));
          handleChange(
            {
              ...{ low: lowerBound.toString(), high: upperBound.toString() },
              [type]: e.target.value,
            },
            type,
          );
        }}
      />
    );
  };

  return (
    <div
      className={classNames(styles.container, styles.rangeSlider, className)}
    >
      <span className={styles.label}>{label}</span>
      <div className={styles.inputContainer}>
        <div className={styles.valueDisplay}>{createTextField('low')}</div>
        {renderInput()}
        <div className={styles.valueDisplay}>{createTextField('high')}</div>
      </div>
    </div>
  );
};
